import { useState, useCallback, useEffect } from 'react';
import type { SelectedExcelFile, ParseExcelResponse } from '@/types/api';
import type { ResolvedPerson } from '@/lib/name-matching';

export type { ResolvedPerson } from '@/lib/name-matching';

export interface TesterMapping {
  id: string;
  domain: string;
  tester: string;          // subdomain or tester role name
  area: string;
  testPersons: ResolvedPerson[];
  subdomain?: string;
  scenarioFiles?: string[]; // scenario xlsx file names that reference this (tester, area) role
}

export interface PersistedWizardState {
  version: 1 | 2 | 3;
  savedAt: string;
  currentStep: number;
  projectName: string;
  projectNumber: string;
  testingPhase: string;
  projectDeadline: string;
  ictResponsible: string;
  selectedTestFiles: SelectedExcelFile[];
  selectedMappingFile: SelectedExcelFile[];
  parseResult: ParseExcelResponse | null;
  testerMappings: TesterMapping[];
  needsReparse?: boolean;
  /** Parse job ID — used to restore full parse results from S3 on draft resume */
  jobId?: string;
}

const STORAGE_KEY = 'lotus-new-project-draft';
const EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours
const STALE_MS = 12 * 60 * 60 * 1000;  // 12 hours
const MAX_SIZE_BYTES = 2 * 1024 * 1024; // 2 MB

type DraftInput = Omit<PersistedWizardState, 'version' | 'savedAt' | 'needsReparse'>;

function loadDraft(): PersistedWizardState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed: PersistedWizardState = JSON.parse(raw);
    if (parsed.version !== 1 && parsed.version !== 2 && parsed.version !== 3) return null;

    // Migrate v1 drafts: add selectedMappingFile if missing
    if (!Array.isArray(parsed.selectedMappingFile)) {
      parsed.selectedMappingFile = [];
    }

    // Migrate v2→v3: convert string[] testPersons to ResolvedPerson[]
    if (parsed.version < 3 && Array.isArray(parsed.testerMappings)) {
      parsed.testerMappings = parsed.testerMappings.map((m) => ({
        ...m,
        testPersons: (m.testPersons as unknown as (string | Record<string, unknown>)[]).map(
          (p): ResolvedPerson =>
            typeof p === 'string'
              ? {
                  excelName: p,
                  excelEmail: null,
                  cognitoUserId: null,
                  cognitoUserName: null,
                  cognitoEmail: null,
                  matchConfidence: null,
                }
              : {
                  excelName: (p as Record<string, unknown>).excelName as string ?? '',
                  excelEmail: (p as Record<string, unknown>).excelEmail as string | null ?? null,
                  cognitoUserId: (p as Record<string, unknown>).cognitoUserId as string | null ?? null,
                  cognitoUserName: (p as Record<string, unknown>).cognitoUserName as string | null ?? null,
                  cognitoEmail: (p as Record<string, unknown>).cognitoEmail as string | null ?? null,
                  matchConfidence: (p as Record<string, unknown>).matchConfidence as ResolvedPerson['matchConfidence'] ?? null,
                },
        ),
      }));
      parsed.version = 3;
    }

    const age = Date.now() - new Date(parsed.savedAt).getTime();
    if (age > EXPIRY_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return parsed;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

function stripLargeParseData(state: PersistedWizardState): PersistedWizardState {
  if (!state.parseResult) return state;

  // First pass: strip test_script arrays (the largest per-scenario data)
  const strippedScenarios = state.parseResult.scenarios.map(s => ({
    ...s,
    test_script: undefined,
  }));

  let stripped: PersistedWizardState = {
    ...state,
    needsReparse: true,
    parseResult: {
      ...state.parseResult,
      scenarios: strippedScenarios,
    },
  };

  // Second pass: if still too large (e.g. 5000+ scenarios), strip per-scenario arrays
  if (new Blob([JSON.stringify(stripped)]).size > MAX_SIZE_BYTES) {
    const minimalScenarios = state.parseResult.scenarios.map(s => ({
      ...s,
      test_script: undefined,
      testers: undefined,
      domains: undefined,
      warnings: undefined,
      description: '',
    }));
    stripped = {
      ...stripped,
      parseResult: {
        ...state.parseResult,
        scenarios: minimalScenarios,
      },
    };
  }

  return stripped;
}

export function useWizardPersistence() {
  const [draft] = useState<PersistedWizardState | null>(() => loadDraft());

  const [isStale, setIsStale] = useState(false);

  useEffect(() => {
    if (!draft) return;
    const age = Date.now() - new Date(draft.savedAt).getTime();
    setIsStale(age > STALE_MS);
  }, [draft]);

  const saveDraft = useCallback((input: DraftInput) => {
    let state: PersistedWizardState = {
      ...input,
      version: 3,
      savedAt: new Date().toISOString(),
    };

    let serialized = JSON.stringify(state);

    if (new Blob([serialized]).size > MAX_SIZE_BYTES) {
      state = stripLargeParseData(state);
      serialized = JSON.stringify(state);
    }

    try {
      localStorage.setItem(STORAGE_KEY, serialized);
    } catch {
      // localStorage full — try with stripped data
      state = stripLargeParseData(state);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      } catch {
        // Give up silently
      }
    }
  }, []);

  const clearDraft = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return { draft, saveDraft, clearDraft, isStale };
}
